import type { FaqEntry } from '../faq';

/**
 * "FAQ — Worldcoo.com", the final document Comms signed off.
 *
 * Transcribed VERBATIM: no summarising, no rewording. Spanish is the SOURCE — the other four locales
 * are translated from this file, which is the opposite of the code, whose `msgid` is English.
 *
 * Two things the document leaves open, kept visible rather than quietly patched:
 *
 *  1. "¿Cómo empezar a colaborar?" has NO answer — only the note "Insertar enlace espacio web donde lo
 *     contamos". The answer below is DRAFTED from what the sign-up actually does and needs Comms'
 *     approval, like the drafted answers on the NGO page's own FAQ.
 *  2. "¿Puedo elegir cuánto dona el cliente?" is answered with the text about choosing a CAUSE, which
 *     is also the answer to the question right after it. Transcribed as written; it reads as a
 *     copy-paste slip in the source and wants a real answer about donation rules.
 *
 * The document's index says four blocks and then writes five; the five are what the page shows.
 */
export const faqEntriesEs: FaqEntry[] = [
	// MARK: 1. Para empresas
	{
		block: 'companies',
		question: '¿Qué es Worldcoo?',
		answer:
			'Worldcoo es una empresa tecnológica que conecta empresas, personas y entidades sociales para facilitar nuevas formas de donación y generar impacto social y medioambiental. Desde 2012 desarrollamos soluciones que permiten integrar la donación en los canales de compra de las empresas y convertir pequeños gestos cotidianos en oportunidades de impacto.',
	},
	{
		block: 'companies',
		question: '¿Qué soluciones ofrece Worldcoo?',
		answer:
			'Nuestro objetivo es facilitar la integración de la solidaridad en los diferentes puntos de contacto entre las empresas y sus clientes o empleados. Worldcoo desarrolla diferentes soluciones de donación que se adaptan a los distintos canales y necesidades de cada empresa. Entre ellas se encuentran el Redondeo Solidario, las donaciones en ecommerce, los portales de donación y las soluciones de donación vinculadas a nóminas o facturas.',
	},
	{
		block: 'companies',
		question: '¿Qué es el Redondeo Solidario?',
		answer:
			'El Redondeo Solidario permite a los clientes de una empresa realizar una pequeña donación al finalizar una compra. El cliente puede redondear el importe de su compra y donar la diferencia a un proyecto social o medioambiental seleccionado.',
	},
	{
		block: 'companies',
		question: '¿Qué coste tiene incorporar Worldcoo?',
		answer:
			'Analizamos cada caso de forma personalizada, teniendo en cuenta las necesidades de la empresa, la solución que se quiera implementar y el tipo de integración tecnológica requerida. En función de la operativa y la implementación necesarias, la incorporación de las soluciones de Worldcoo puede ser gratuita.',
	},
	{
		block: 'companies',
		question: '¿Tengo que cambiar mi sistema de pago para incorporar Worldcoo?',
		answer:
			'Worldcoo trabaja con diferentes proveedores tecnológicos y sistemas de pago para integrar sus soluciones en los canales de cada empresa. Nuestro equipo analiza cada caso para identificar la mejor opción de integración.',
	},
	{
		block: 'companies',
		question: '¿En qué canales puedo activar Worldcoo?',
		answer:
			'Las soluciones de Worldcoo pueden integrarse en diferentes puntos de contacto con el cliente, tanto físicos como digitales, y también con empleados:',
		bullets: [
			'Tiendas y establecimientos físicos.',
			'Ecommerce.',
			'Apps.',
			'Portales de Donación online',
			'Nóminas y facturas',
		],
		outro: 'La solución se adapta a las características y necesidades de cada empresa.',
	},
	{
		block: 'companies',
		question: '¿Puedo elegir cuánto dona el cliente?',
		answer:
			'Sí. Trabajamos con cada empresa para identificar proyectos sociales o medioambientales alineados con sus valores, objetivos y prioridades. Worldcoo propone una selección de proyectos que pueden encajar con cada compañía, pero la decisión final sobre qué causa apoyar siempre corresponde a la empresa.',
	},
	{
		block: 'companies',
		question: '¿Puedo elegir qué causa o proyecto quiero apoyar?',
		answer:
			'Sí. Worldcoo trabaja con las empresas para identificar proyectos que estén alineados con sus valores, objetivos de impacto y prioridades sociales o medioambientales. Worldcoo presenta una propuesta de proyectos que cree que pueden encajar con cada compañía en concreto, pero es la empresa quien tiene la última palabra sobre el proyecto elegido.',
	},
	{
		block: 'companies',
		question: '¿Puedo apoyar proyectos locales?',
		answer:
			'Sí. En Worldcoo trabajamos con entidades sociales de diferentes tamaños y ámbitos de actuación, incluidas organizaciones locales que desarrollan proyectos con un gran impacto en sus comunidades. Uno de nuestros objetivos es facilitar también el acceso a financiación a estas entidades y dar visibilidad a la importante labor que realizan.',
	},
	{
		block: 'companies',
		question: '¿Puedo cambiar de proyecto?',
		answer:
			'Sí. Las campañas pueden actualizarse cuando un proyecto alcanza su objetivo de financiación, finaliza o cuando la empresa decide apoyar una nueva causa.',
	},
	{
		block: 'companies',
		question: '¿Puedo saber cuánto se ha recaudado?',
		answer:
			'Sí. Worldcoo ofrece herramientas de seguimiento que permiten conocer la evolución de las campañas y el impacto generado de forma diaria y segmentada por establecimientos.',
	},
	{
		block: 'companies',
		question: '¿Puedo comunicar los resultados de la campaña?',
		answer:
			'Sí. Worldcoo proporciona información sobre la evolución de las campañas y los proyectos financiados para facilitar la comunicación del impacto generado.',
	},

	// MARK: 2. Para entidades sociales
	{
		block: 'ngos',
		question: '¿Cómo puede mi entidad recaudar fondos a través de Worldcoo?',
		answer:
			'Las entidades sociales pueden presentar sus proyectos a Worldcoo para optar a formar parte de las campañas de recaudación que se activan a través de empresas colaboradoras. Nuestro equipo analiza cada propuesta y, cuando existe una oportunidad de colaboración, trabaja con la entidad para poner en marcha la campaña.',
	},
	{
		block: 'ngos',
		question: '¿Qué tipo de proyectos financia Worldcoo?',
		answer: 'Trabajamos con proyectos de diferentes ámbitos sociales y medioambientales, entre ellos:',
		bullets: [
			'Infancia y juventud.',
			'Inclusión social.',
			'Salud.',
			'Personas mayores.',
			'Discapacidad.',
			'Medio ambiente.',
			'Cooperación.',
			'Emergencias.',
			'Otros ámbitos de impacto social.',
		],
	},
	{
		block: 'ngos',
		question: '¿Qué requisitos debe cumplir una entidad para colaborar con Worldcoo?',
		answer:
			'Las entidades deben cumplir los criterios de selección establecidos por Worldcoo y aportar la documentación necesaria para acreditar su constitución, actividad y situación legal. Además, deben tener capacidad para emitir los certificados de donación correspondientes.',
		outro: 'Puedes consultar los criterios de selección de Worldcoo para conocer todos los requisitos.',
		link: { label: 'Criterios de selección', target: 'selection-criteria' },
	},
	{
		block: 'ngos',
		question: '¿Cómo empezar a colaborar?',
		// DRAFTED — the document has no answer here, only the note to insert the link. See the file header.
		answer: 'Puedes iniciar el proceso rellenando el formulario de alta con la información básica de tu entidad.',
		link: { label: 'Ir al formulario de alta', target: 'ngo-signup' },
	},
	{
		block: 'ngos',
		question: '¿Puedo presentar un proyecto concreto?',
		answer:
			'Sí. Worldcoo ayuda a financiar proyectos concretos de las entidades sociales, no a financiar a las entidades de forma general. Puedes compartir con nosotros uno o varios proyectos que necesiten financiación. Nuestro equipo valorará su adecuación a los criterios de colaboración y buscará las oportunidades que mejor puedan encajar con cada proyecto.',
	},
	{
		block: 'ngos',
		question: '¿Pueden colaborar entidades pequeñas o locales?',
		answer:
			'Sí. En Worldcoo queremos contribuir a que también las entidades locales y de menor tamaño puedan acceder a nuevas oportunidades de financiación. Muchas de estas organizaciones realizan una labor esencial en sus comunidades y generan un impacto directo muy relevante.',
	},
	{
		block: 'ngos',
		question: '¿Cómo funciona el proceso de elección de proyectos?',
		answer:
			'Una vez formes parte de la comunidad de Worldcoo, tus proyectos pasan a formar parte de nuestra base de datos. Cuando surge una oportunidad de colaboración, presentamos los proyectos que mejor pueden encajar con las necesidades y criterios de cada empresa, teniendo en cuenta aspectos como el ámbito territorial y temático, su estrategia de RSC o el perfil de sus clientes.',
		outro:
			'La decisión final sobre qué organización colaborará en cada campaña corresponde siempre a la empresa colaboradora.',
	},
	{
		block: 'ngos',
		question: 'Una vez financiado un proyecto, ¿cómo se gestionan los fondos recaudados?',
		answer:
			'Una vez finaliza la campaña de recaudación, Worldcoo gestiona la transferencia del 100% de los fondos a la entidad social beneficiaria. El proceso se realiza en dos pagos:',
		bullets: [
			'Un primer pago del 50 % del importe recaudado al finalizar la campaña.',
			'Un segundo pago del 50 % restante una vez la entidad presente el informe de seguimiento y justificación del proyecto.',
		],
	},
	{
		block: 'ngos',
		question: '¿Qué compromisos adquiere una entidad cuando recibe financiación?',
		answer:
			'Las entidades que reciben fondos a través de Worldcoo se comprometen a informar sobre la ejecución y el impacto del proyecto financiado. Para ello, deben presentar un informe que incluya información sobre:',
		bullets: [
			'El desarrollo del proyecto y las personas beneficiarias.',
			'Los resultados e impacto conseguido.',
			'Imágenes u otra documentación que permita conocer la evolución del proyecto.',
			'El uso y destino de los fondos recibidos.',
		],
	},
	{
		block: 'ngos',
		question: '¿Qué coste tiene colaborar con Worldcoo para una entidad social?',
		answer:
			'Worldcoo ofrece a las entidades sociales servicios de captación, gestión y seguimiento de fondos, así como acceso a nuevas oportunidades de colaboración con empresas. Por estos servicios, Worldcoo aplica una tarifa sobre los fondos gestionados. Las condiciones se comunican de forma transparente a cada entidad antes del inicio de cualquier colaboración.',
	},

	// MARK: 3. Para personas donantes
	{
		block: 'donors',
		question: '¿Qué es el Redondeo Solidario?',
		answer:
			'El Redondeo Solidario es una forma sencilla de hacer una pequeña donación al realizar una compra. Al pagar, puedes redondear el importe de tu compra y destinar esos céntimos a un proyecto social o medioambiental.',
	},
	{
		block: 'donors',
		question: '¿Dónde puedo hacer una donación?',
		answer:
			'Puedes encontrar soluciones de donación de Worldcoo en diferentes establecimientos y canales digitales de nuestras empresas colaboradoras.',
	},
	{
		block: 'donors',
		question: '¿Cuánto dinero dono?',
		answer:
			'El importe de la donación depende de la solución y de la campaña en la que participes. En el caso del Redondeo Solidario, puedes redondear el importe de tu compra y donar la diferencia. En otras campañas, puedes realizar una donación de un importe fijo previamente establecido. Antes de confirmar tu donación, siempre podrás conocer el importe que vas a aportar.',
	},
	{
		block: 'donors',
		question: '¿Puedo decidir si quiero donar?',
		answer:
			'Sí. La participación en el Redondeo Solidario es voluntaria y anónima. Antes de completar la donación, el sistema te informa del importe que puedes aportar.',
	},
	{
		block: 'donors',
		question: '¿A qué proyecto se destina mi donación?',
		answer:
			'Tu donación se destina al proyecto asociado a la campaña en la que estás participando. Puedes consultar información sobre el proyecto, la entidad responsable y la evolución de la campaña a través de los canales habilitados por Worldcoo y por cada empresa.',
	},
	{
		block: 'donors',
		question: '¿Cómo puedo saber cuánto se ha recaudado?',
		answer:
			'Worldcoo ofrece información sobre los objetivos de cada campaña y su evolución para facilitar el seguimiento de las donaciones y del impacto generado.',
	},
	{
		block: 'donors',
		question: '¿Qué pasa con mi dinero después de hacer una donación?',
		answer:
			'Una vez realizada la donación, Worldcoo gestiona los fondos recaudados durante la campaña y realiza su seguimiento. Al finalizar la campaña, los fondos se destinan a la entidad social responsable del proyecto para contribuir a su financiación. Posteriormente, la entidad informa sobre el desarrollo y el impacto conseguido gracias a los fondos recaudados.',
	},
	{
		block: 'donors',
		question: '¿Puedo obtener un certificado de mi donación?',
		answer:
			'Sí. Puedes solicitar tu certificado de donación a través de app.worldcoo.com. Para hacerlo, necesitarás conservar el ticket o comprobante de tu donación, donde encontrarás el código de donación necesario para identificarla.',
	},
	{
		block: 'donors',
		question: '¿Qué es el código de donación?',
		answer:
			'El código de donación es un identificador único asociado a tu aportación. Puedes encontrarlo en el ticket o comprobante de donación y utilizarlo para registrar tu donación en app.worldcoo.com y solicitar, cuando corresponda, tu certificado de donación.',
	},
	{
		block: 'donors',
		question: '¿Cómo puedo conocer todas las donaciones que he realizado?',
		answer:
			'A través de app.worldcoo.com puedes registrar y consultar las donaciones que has realizado. La plataforma te permite tener un histórico de tus donaciones y solicitar los certificados correspondientes.',
	},
	{
		block: 'donors',
		question: '¿Puedo cancelar o solicitar la devolución de una donación?',
		answer:
			'Sí. Puedes solicitar la devolución de una donación. El dinero donado lo gestiona directamente Worldcoo, así que Worldcoo se ocupará de hacer la devolución del importe donado bajo petición del cliente.',
		outro: 'Entra en nuestro apartado de contacto y escríbenos con tu caso.',
		link: { label: 'Contacta con nosotros', target: 'contact-donor' },
	},

	// MARK: 4. Transparencia y seguridad
	{
		block: 'transparency',
		question: '¿Cómo garantiza Worldcoo la transparencia de las donaciones?',
		answer:
			'La transparencia es uno de los pilares de nuestro modelo. Worldcoo realiza el seguimiento de las campañas y ofrece información sobre las cantidades recaudadas, los proyectos financiados y las entidades participantes. Además, publicamos periódicamente información sobre nuestra actividad y el impacto generado.',
	},
	{
		block: 'transparency',
		question: '¿Cuánto dinero llega a las entidades sociales?',
		answer:
			'El 100 % de las donaciones recaudadas en cada campaña se destina al proyecto y a la entidad social responsable de llevarlo a cabo. Posteriormente, la ONG se encarga de realizar un pago a Worldcoo por los servicios prestados.',
	},
	{
		block: 'transparency',
		question: '¿Cuántos proyectos ha financiado Worldcoo?',
		answer:
			'Desde nuestra creación, Worldcoo ha contribuido a financiar más de 1.000 proyectos sociales y medioambientales, en colaboración con cientos de entidades.',
	},
	{
		block: 'transparency',
		question: '¿Cuántas donaciones se han realizado a través de Worldcoo?',
		answer:
			'Millones de personas han participado en campañas de Worldcoo y se han realizado más de 100 millones de microdonaciones.',
	},
	{
		block: 'transparency',
		question: '¿Dónde puedo consultar el impacto de Worldcoo?',
		answer:
			'Puedes consultar información sobre las campañas, proyectos e impacto generado por Worldcoo a través de nuestra web. También publicamos periódicamente información sobre nuestra actividad y nuestra memoria de impacto.',
	},
	{
		block: 'transparency',
		question: '¿Es seguro donar a través de Worldcoo?',
		answer:
			'Sí. Las soluciones de Worldcoo están integradas en los sistemas de pago de nuestras empresas colaboradoras y se desarrollan siguiendo los estándares de seguridad aplicables. Además, Worldcoo realiza un seguimiento de las campañas y de los fondos recaudados para garantizar una gestión transparente de las donaciones.',
	},
	{
		block: 'transparency',
		question: '¿Worldcoo guarda los datos de mi tarjeta?',
		answer: 'Worldcoo no necesita almacenar los datos completos de tu tarjeta para que puedas realizar una donación.',
	},
	{
		block: 'transparency',
		question: '¿Cómo se utilizan mis datos personales?',
		answer:
			'Los datos personales se tratan de acuerdo con nuestra Política de Privacidad y con la normativa vigente en materia de protección de datos.',
		link: { label: 'Política de Privacidad', target: 'privacy-policy' },
	},

	// MARK: 5. Sobre Worldcoo
	{
		block: 'about',
		question: '¿Cuándo nació Worldcoo?',
		answer:
			'Worldcoo nació en 2012 con el objetivo de utilizar la tecnología para facilitar la financiación de proyectos sociales y medioambientales.',
	},
	{
		block: 'about',
		question: '¿Dónde opera Worldcoo?',
		answer:
			'Worldcoo trabaja con empresas y entidades sociales en diferentes mercados y canaliza fondos hacia proyectos tanto nacionales como internacionales.',
	},
	{
		block: 'about',
		question: '¿Worldcoo es una ONG?',
		answer:
			'No. Worldcoo es una empresa tecnológica especializada en soluciones de microdonación. Nuestro objetivo es conectar empresas, personas y entidades sociales para facilitar la colaboración y generar impacto.',
	},
	{
		block: 'about',
		question: '¿Worldcoo es una empresa B Corp?',
		answer:
			'Sí. Worldcoo cuenta con la certificación B Corp, que reconoce el compromiso de las empresas con elevados estándares de impacto social y medioambiental, transparencia y responsabilidad.',
	},
	{
		block: 'about',
		question: '¿Por qué Worldcoo?',
		answer:
			'Porque creemos que donar debería ser fácil. En Worldcoo utilizamos la tecnología para conectar empresas, personas y entidades sociales y convertir millones de pequeños gestos cotidianos en oportunidades de impacto. Desde 2012 trabajamos para hacer que la colaboración social sea cada vez más sencilla, accesible y transparente.',
	},
];
